import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import * as React from 'react';

import { DatePicker } from '@/components/DatePicker';
import { ChevronDownIcon, ClockIcon, XIcon } from '@/imports/ui/chakra/feather';

import { useDefaultScheduledPostOptions } from './hooks/useDefaultScheduledPostOptions';

dayjs.extend(advancedFormat);

export const SchedulePostMenu = React.forwardRef<
  HTMLInputElement,
  {
    isDisabled?: boolean;
    value: Date | null;
    onChange: (value: Date | null) => void;
    onBlur: () => void;
    /** Validation function that will only run when the custom date modal is open */
    validate: () => boolean;
    validationMessage?: string;
    shouldRestrictUnsettingValue?: boolean;
  }
>((props, ref) => {
  const customDateTimeModal = useDisclosure();
  const scheduledPostTimings = useDefaultScheduledPostOptions();

  const timezone = dayjs().format('zzz');
  const timezoneAbbreviation = dayjs().format('z');

  /**
   * Used to keep track of the date value present when the custom date selector is opened.
   * This is needed to "reset" the value is the user cancels out of the date picker.
   */
  const [initialDateValue, setInitialDateValue] = React.useState(props.value);

  return (
    <>
      <Modal
        isOpen={customDateTimeModal.isOpen}
        onClose={customDateTimeModal.onClose}
        blockScrollOnMount={false}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Set Date and Time
            <Text fontWeight="normal">
              {timezone} ({timezoneAbbreviation})
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isRequired isInvalid={!!props.validationMessage}>
              <FormLabel>Date & Time</FormLabel>
              <DatePicker
                customInput={<Input ref={ref} />}
                selected={props.value}
                onChange={props.onChange}
                onBlur={props.onBlur}
                autoComplete="off"
                minDate={dayjs().toDate()}
                dateFormat="MMM d, yyyy h:mmaa"
                showTimeSelect
                dropdownMode="select"
              />
              <FormErrorMessage>{props.validationMessage}</FormErrorMessage>
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <HStack>
              <Button
                variant="outline"
                colorScheme="gray"
                onClick={() => {
                  props.onChange(initialDateValue);
                  customDateTimeModal.onClose();
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  const isValid = props.validate();

                  if (isValid) {
                    customDateTimeModal.onClose();
                  }
                }}
              >
                Set Schedule
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Menu placement="top">
        <MenuButton
          as={Button}
          rightIcon={<ChevronDownIcon />}
          leftIcon={!!props.value ? <ClockIcon /> : undefined}
          variant="outline"
          colorScheme="gray"
          backgroundColor="gray.50"
          disabled={props.isDisabled}
        >
          {!!props.value
            ? dayjs(props.value).format('MMM D, h:mm A')
            : 'Schedule for Later'}
        </MenuButton>
        <MenuList>
          {scheduledPostTimings.map(({ label, date }, i) => (
            <MenuItem
              key={i}
              onClick={() => {
                props.onChange(date.toDate());
              }}
            >
              <Stack spacing="0">
                <Text>{label}</Text>
                <Text textStyle="compact" fontWeight="normal">
                  {date.format('dddd, MMM D, h:mm A z')}
                </Text>
              </Stack>
            </MenuItem>
          ))}
          <MenuDivider my="1" />
          <MenuItem
            fontWeight="normal"
            onClick={() => {
              setInitialDateValue(props.value);
              customDateTimeModal.onOpen();
            }}
            icon={<ClockIcon />}
          >
            Set Custom Time
          </MenuItem>
          {!props.shouldRestrictUnsettingValue && !!props.value && (
            <>
              <MenuDivider my="1" />
              <MenuItem
                fontWeight="normal"
                onClick={() => {
                  props.onChange(null);
                }}
                icon={<XIcon />}
              >
                Clear Scheduled Time
              </MenuItem>
            </>
          )}
        </MenuList>
      </Menu>
    </>
  );
});

SchedulePostMenu.displayName = 'SchedulePostMenu';
